// Shadow Network — Direct Messages: conversation list.
// Route: /shadow/mensajes
// Polls every 15s to refresh unread counts and pick up new conversations.
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MessageSquare, Search, X as XIcon } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { tShadow } from "../i18n/shadow-social";
import { UserAvatar } from "../components/shadow/UserAvatar";
import { API_BASE as API } from "../lib/api";

const POLL_MS = 15000;

const formatTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "ahora";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d`;
    return d.toLocaleDateString();
  } catch {
    return "";
  }
};

const truncate = (s, n = 60) => {
  if (!s) return "";
  return s.length > n ? s.slice(0, n).trim() + "…" : s;
};

const ShadowMessagesPage = () => {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- User search (additive) -----------------------------------------------
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchAbort = useRef(null);
  const searchTimer = useRef(null);

  const runSearch = useCallback(async (q) => {
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    if (searchAbort.current) searchAbort.current.cancel?.("new-query");
    const source = axios.CancelToken.source();
    searchAbort.current = source;
    setSearching(true);
    try {
      const { data } = await axios.get(
        `${API}/api/shadow/social/search?q=${encodeURIComponent(trimmed)}&limit=8`,
        { cancelToken: source.token }
      );
      setResults(Array.isArray(data?.users) ? data.users : []);
    } catch (err) {
      if (!axios.isCancel(err)) setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const onQueryChange = (val) => {
    setQuery(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => runSearch(val), 220);
  };

  const clearSearch = () => {
    setQuery("");
    setResults([]);
  };

  const fetchConversations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/shadow/messages`);
      setConversations(data?.conversations || []);
    } catch {
      /* silent — polling will retry */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    fetchConversations();
    const id = setInterval(fetchConversations, POLL_MS);
    return () => clearInterval(id);
  }, [user, fetchConversations]);

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-sm text-white/65 mb-4">Acceso de operador requerido.</p>
          <Link to="/" className="text-amber-400 underline">Volver</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="shadow-messages-list">
      <header className="border-b border-white/5 bg-black/70 backdrop-blur-xl sticky top-0 z-30">
        <div className="mx-auto flex max-w-2xl items-center gap-4 px-4 py-4 md:px-8">
          <Link
            to="/shadow"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            data-testid="messages-back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-[0.2em]">Shadow</span>
          </Link>
          <h1 className="flex-1 text-center text-base font-bold uppercase tracking-[0.18em]">
            {tShadow("mensajes", lang)}
          </h1>
          <span className="w-20" />
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-6 md:px-8">
        {/* Encryption badge (purely informational) */}
        <div
          className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-emerald-400/25 bg-emerald-400/[0.08] px-2.5 py-1"
          data-testid="messages-encryption-badge"
          role="note"
          aria-label="AES-256 encrypted messages"
        >
          <span aria-hidden="true" className="text-[10px]">🔒</span>
          <span className="font-mono text-[9px] tracking-[0.1em] text-emerald-300/80">
            {tShadow("cifradoBadge", lang)}
          </span>
        </div>

        {/* User search — additive, sits above the conversations list */}
        <div className="relative mb-4" data-testid="messages-search">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
          <input
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Buscar trader para mensajear…"
            data-testid="messages-search-input"
            className="w-full rounded-lg border border-amber-400/25 bg-white/[0.04] py-2.5 pl-9 pr-9 font-mono text-xs text-white placeholder-white/35 outline-none transition-colors focus:border-amber-400/60"
          />
          {query && (
            <button
              type="button"
              onClick={clearSearch}
              aria-label="Limpiar búsqueda"
              data-testid="messages-search-clear"
              className="absolute right-2 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-white/40 hover:bg-white/10 hover:text-white"
            >
              <XIcon className="h-3.5 w-3.5" />
            </button>
          )}

          {(searching || results.length > 0) && query.trim().length >= 2 && (
            <div
              className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-lg border border-amber-400/25 bg-zinc-950/95 shadow-2xl backdrop-blur-xl"
              data-testid="messages-search-results"
            >
              {searching && results.length === 0 && (
                <p className="px-4 py-3 text-center font-mono text-[11px] text-white/45">
                  Buscando…
                </p>
              )}
              {!searching && results.length === 0 && (
                <p className="px-4 py-3 text-center font-mono text-[11px] text-white/45">
                  Sin resultados
                </p>
              )}
              {results.map((u) => (
                <Link
                  key={u.id || u._id}
                  to={`/shadow/mensajes/${u.id || u._id}`}
                  data-testid={`messages-search-result-${u.id || u._id}`}
                  onClick={clearSearch}
                  className="flex items-center gap-3 border-b border-white/5 px-3 py-2.5 transition-colors last:border-0 hover:bg-white/[0.04]"
                >
                  <UserAvatar user={u} size="sm" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-[13px] font-semibold text-white">
                        {u.display_name || u.name}
                      </span>
                      {u.is_pro && (
                        <span className="rounded-full border border-amber-400/40 bg-amber-400/10 px-1.5 py-px text-[8px] font-black tracking-widest text-amber-200">
                          PRO
                        </span>
                      )}
                    </div>
                    {u.username && (
                      <span className="text-[11px] text-white/40">@{u.username}</span>
                    )}
                  </div>
                  <span className="ml-2 shrink-0 font-mono text-[10px] text-amber-300">
                    💬 Mensajear →
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <p className="text-center text-white/45 text-sm font-mono py-12">…</p>
        ) : conversations.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center">
            <MessageSquare className="mx-auto h-8 w-8 text-white/30 mb-3" />
            <p className="text-sm text-white/55">{tShadow("sinConversaciones", lang)}</p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="conversations-list">
            {conversations.map((c) => {
              const peer = c.peer || {};
              const last = c.last_message || {};
              const mine = last.sender_id && user && last.sender_id === user.id;
              const href = peer.id ? `/shadow/mensajes/${peer.id}` : "#";
              return (
                <Link
                  key={peer.id || Math.random()}
                  to={href}
                  data-testid={`conversation-row-${peer.id}`}
                  className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 transition-all hover:border-amber-400/30 hover:bg-white/[0.05]"
                >
                  <UserAvatar user={peer} size="md" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-white truncate">
                        {peer.name}
                        {peer.username && (
                          <span className="ml-1.5 text-[11px] text-white/40">
                            @{peer.username}
                          </span>
                        )}
                      </span>
                      <span className="text-[10px] text-white/35 font-mono shrink-0">
                        {formatTime(last.created_at)}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-white/55 truncate">
                      {mine && <span className="text-white/35">Tú: </span>}
                      {truncate(last.content)}
                    </p>
                  </div>
                  {c.unread > 0 && (
                    <span
                      className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-400 px-1.5 text-[10px] font-black text-black"
                      data-testid={`conversation-unread-${peer.id}`}
                    >
                      {c.unread}
                    </span>
                  )}
                </Link>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
};

export default ShadowMessagesPage;
